import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Loader2, Save, Plus, Trash2 } from 'lucide-react';
import { ImageUploadField } from './ImageUploadField';
import { RichTextEditor } from './RichTextEditor';

/**
 * Section editor dialog (WordPress-like block editor).
 * Each section_type renders a tailored UI; layout & style controls are shared.
 */
export function SectionEditorDialog({
  open,
  onOpenChange,
  sectionForm,
  setSectionForm,
  saveSection,
  saving,
  editingSection,
  sectionTypes,
  api,
}) {
  const update = (patch) => setSectionForm({ ...sectionForm, ...patch });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#041A3B] border-white/10 max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white">
            {editingSection ? 'Edit Block' : 'Add Block'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Block Type Picker */}
          <div className="space-y-2">
            <Label className="text-white">Block Type</Label>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {sectionTypes.map((type) => {
                const Icon = type.icon;
                const isActive = sectionForm.section_type === type.type;
                return (
                  <button
                    key={type.type}
                    type="button"
                    onClick={() => update({ section_type: type.type })}
                    data-testid={`block-type-${type.type}`}
                    className={`p-2 rounded-lg border text-xs flex flex-col items-center gap-1 transition-all ${
                      isActive
                        ? 'border-[#00E5FF] bg-[#00E5FF]/10 text-white'
                        : 'border-white/10 bg-[#000B18] text-[#A0B4CC] hover:border-white/30'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-[10px] text-center leading-tight">{type.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Common: title / subtitle (visible for all except pure html/divider/spacer) */}
          {!['html', 'divider', 'spacer', 'image', 'video'].includes(sectionForm.section_type) && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Title</Label>
                <Input
                  value={sectionForm.title || ''}
                  onChange={(e) => update({ title: e.target.value })}
                  className="bg-[#000B18] border-white/20 text-white"
                  placeholder="Block title"
                  data-testid="section-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Subtitle</Label>
                <Input
                  value={sectionForm.subtitle || ''}
                  onChange={(e) => update({ subtitle: e.target.value })}
                  className="bg-[#000B18] border-white/20 text-white"
                  placeholder="Optional subtitle"
                />
              </div>
            </div>
          )}

          {/* Block-specific fields */}
          <BlockSpecificFields sectionForm={sectionForm} update={update} api={api} />

          {/* CTA buttons (shared for hero/cta/content/features) */}
          {['hero', 'cta', 'content', 'features', 'columns'].includes(sectionForm.section_type) && (
            <CTAButtons sectionForm={sectionForm} update={update} />
          )}

          {/* Layout & Style (shared) */}
          <LayoutStyleControls sectionForm={sectionForm} update={update} api={api} />

          <div className="flex items-center justify-between p-3 rounded-lg bg-[#000B18]">
            <Label className="text-white">Visible</Label>
            <Switch
              checked={sectionForm.is_visible !== false}
              onCheckedChange={(checked) => update({ is_visible: checked })}
              data-testid="section-visible-switch"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-[#A0B4CC]">Cancel</Button>
          <Button onClick={saveSection} disabled={saving} className="btn-primary" data-testid="save-section-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Block
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Block-specific fields ---------------- */
function BlockSpecificFields({ sectionForm, update, api }) {
  const t = sectionForm.section_type;

  if (t === 'content' || t === 'hero' || t === 'cta') {
    return (
      <div className="space-y-2">
        <Label className="text-white">Content</Label>
        <RichTextEditor
          value={sectionForm.content || ''}
          onChange={(html) => update({ content: html })}
          placeholder="Write your content… use the toolbar for formatting."
        />
        <p className="text-[#5C7599] text-[11px]">Rich text & HTML supported. Toggle the “HTML” button to edit raw markup.</p>
      </div>
    );
  }

  if (t === 'html') {
    return (
      <div className="space-y-2">
        <Label className="text-white">Raw HTML / Embed</Label>
        <Textarea
          value={sectionForm.html || ''}
          onChange={(e) => update({ html: e.target.value })}
          className="bg-[#000B18] border-white/20 text-white font-mono text-xs"
          rows={12}
          placeholder='<div class="my-block">…paste any HTML, iframe, or embed code here…</div>'
          data-testid="html-block-input"
        />
        <p className="text-[#5C7599] text-[11px]">This will be rendered verbatim. Only trusted admins can edit CMS — do not paste untrusted scripts.</p>
      </div>
    );
  }

  if (t === 'image') {
    return (
      <div className="space-y-3">
        <ImageUploadField
          label="Image"
          value={sectionForm.image_url || ''}
          onChange={(url) => update({ image_url: url })}
          api={api}
        />
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-[#A0B4CC] text-xs">Caption (optional)</Label>
            <Input
              value={sectionForm.image_caption || ''}
              onChange={(e) => update({ image_caption: e.target.value })}
              className="bg-[#000B18] border-white/20 text-white text-sm"
              placeholder="Photo caption"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[#A0B4CC] text-xs">Click-through URL (optional)</Label>
            <Input
              value={sectionForm.image_link || ''}
              onChange={(e) => update({ image_link: e.target.value })}
              className="bg-[#000B18] border-white/20 text-white text-sm"
              placeholder="/pricing or https://…"
            />
          </div>
        </div>
      </div>
    );
  }

  if (t === 'video') {
    return (
      <div className="space-y-2">
        <Label className="text-white">Video URL</Label>
        <Input
          value={sectionForm.video_url || ''}
          onChange={(e) => update({ video_url: e.target.value })}
          className="bg-[#000B18] border-white/20 text-white"
          placeholder="YouTube / Vimeo / .mp4 URL"
          data-testid="video-url-input"
        />
        <p className="text-[#5C7599] text-[11px]">YouTube & Vimeo embed URLs are auto-detected. Direct .mp4/.webm play with native &lt;video&gt;.</p>
      </div>
    );
  }

  if (t === 'gallery') {
    return <ItemsArrayEditor sectionForm={sectionForm} update={update} type="gallery" />;
  }

  if (t === 'features' || t === 'faq' || t === 'testimonials' || t === 'pricing') {
    return <ItemsArrayEditor sectionForm={sectionForm} update={update} type={t} />;
  }

  if (t === 'columns') {
    return <ColumnsEditor sectionForm={sectionForm} update={update} api={api} />;
  }

  if (t === 'divider' || t === 'spacer') {
    return (
      <div className="rounded-lg bg-[#000B18] border border-white/10 p-4 text-center text-[#5C7599] text-sm">
        This block has no content — use the layout controls below to adjust spacing/colour.
      </div>
    );
  }

  // Fallback (e.g., user types in a custom section_type)
  return (
    <div className="space-y-2">
      <Label className="text-white">Content</Label>
      <Textarea
        value={sectionForm.content || ''}
        onChange={(e) => update({ content: e.target.value })}
        className="bg-[#000B18] border-white/20 text-white"
        rows={5}
      />
    </div>
  );
}

/* ---------------- Items / cards editor ---------------- */
function ItemsArrayEditor({ sectionForm, update, type }) {
  const items = Array.isArray(sectionForm.items) ? sectionForm.items : [];

  const blankItem = () => {
    // Stable `_uid` so React keys are stable across reorder/delete and the
    // controlled inputs inside each row don't get rebound to a sibling's state.
    const _uid = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `i_${Math.random().toString(36).slice(2)}`;
    if (type === 'faq') return { _uid, question: '', answer: '' };
    if (type === 'pricing') return { _uid, title: '', price: '', description: '', features: [] };
    if (type === 'testimonials') return { _uid, name: '', role: '', quote: '', avatar: '' };
    if (type === 'gallery') return { _uid, url: '', title: '' };
    return { _uid, title: '', description: '' };
  };

  const setItem = (i, patch) => {
    const next = items.map((it, idx) => (idx === i ? { ...it, ...patch } : it));
    update({ items: next });
  };
  const addItem = () => update({ items: [...items, blankItem()] });
  const removeItem = (i) => update({ items: items.filter((_, idx) => idx !== i) });
  const moveItem = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    update({ items: next });
  };

  const labelFor = (key) => ({
    title: 'Title', description: 'Description', question: 'Question', answer: 'Answer',
    name: 'Name', role: 'Role / Title', quote: 'Quote', avatar: 'Avatar URL',
    price: 'Price', url: 'Image URL',
  }[key] || key);

  const keysFor = () => {
    if (type === 'faq') return ['question', 'answer'];
    if (type === 'testimonials') return ['name', 'role', 'quote', 'avatar'];
    if (type === 'pricing') return ['title', 'price', 'description'];
    if (type === 'gallery') return ['url', 'title'];
    return ['title', 'description'];
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-white">Items ({items.length})</Label>
        <Button type="button" size="sm" onClick={addItem} className="btn-secondary h-7 text-xs" data-testid="add-item-btn">
          <Plus className="w-3 h-3 mr-1" /> Add Item
        </Button>
      </div>

      {items.length === 0 && (
        <p className="text-[#5C7599] text-xs italic">No items yet — click “Add Item” to start.</p>
      )}

      {items.map((it, i) => (
        <div key={it._uid || `idx-${i}`} className="rounded-lg border border-white/10 bg-[#000B18] p-3 space-y-2" data-testid={`item-row-${i}`}>
          <div className="flex items-center justify-between">
            <span className="text-[#5C7599] text-xs">#{i + 1}</span>
            <div className="flex items-center gap-1">
              <Button type="button" size="sm" variant="ghost" className="h-6 px-2 text-xs text-[#A0B4CC]" onClick={() => moveItem(i, -1)}>↑</Button>
              <Button type="button" size="sm" variant="ghost" className="h-6 px-2 text-xs text-[#A0B4CC]" onClick={() => moveItem(i, 1)}>↓</Button>
              <Button type="button" size="sm" variant="ghost" className="h-6 px-2 text-[#FF1744]" onClick={() => removeItem(i)} data-testid={`remove-item-${i}`}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </div>
          {keysFor().map((k) => (
            k === 'answer' || k === 'description' || k === 'quote' ? (
              <div key={k} className="space-y-1">
                <Label className="text-[#A0B4CC] text-[11px]">{labelFor(k)}</Label>
                <Textarea
                  value={it[k] || ''}
                  onChange={(e) => setItem(i, { [k]: e.target.value })}
                  rows={3}
                  className="bg-[#041A3B] border-white/15 text-white text-sm"
                />
              </div>
            ) : (
              <div key={k} className="space-y-1">
                <Label className="text-[#A0B4CC] text-[11px]">{labelFor(k)}</Label>
                <Input
                  value={it[k] || ''}
                  onChange={(e) => setItem(i, { [k]: e.target.value })}
                  className="bg-[#041A3B] border-white/15 text-white text-sm"
                />
              </div>
            )
          ))}
        </div>
      ))}
    </div>
  );
}

/* ---------------- Columns editor ---------------- */
function ColumnsEditor({ sectionForm, update, api }) {
  const cols = Array.isArray(sectionForm.columns) ? sectionForm.columns : [];
  const count = sectionForm.columns_count || 2;

  const setCol = (i, patch) => update({ columns: cols.map((c, idx) => idx === i ? { ...c, ...patch } : c) });
  const addCol = () => {
    const _uid = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `c_${Math.random().toString(36).slice(2)}`;
    update({ columns: [...cols, { _uid, title: '', content: '', image: '' }] });
  };
  const removeCol = (i) => update({ columns: cols.filter((_, idx) => idx !== i) });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-white">Columns ({cols.length})</Label>
        <div className="flex items-center gap-2">
          <Label className="text-[#A0B4CC] text-xs">Per-row:</Label>
          <select
            value={count}
            onChange={(e) => update({ columns_count: parseInt(e.target.value, 10) })}
            className="bg-[#000B18] border border-white/20 text-white rounded px-2 py-1 text-xs"
            data-testid="columns-count-select"
          >
            <option value={2}>2</option>
            <option value={3}>3</option>
            <option value={4}>4</option>
          </select>
          <Button type="button" size="sm" onClick={addCol} className="btn-secondary h-7 text-xs">
            <Plus className="w-3 h-3 mr-1" /> Add Column
          </Button>
        </div>
      </div>

      {cols.length === 0 && (
        <p className="text-[#5C7599] text-xs italic">No columns yet — add 2–6 columns to build a multi-column block.</p>
      )}

      {cols.map((c, i) => (
        <div key={c._uid || `col-${i}`} className="rounded-lg border border-white/10 bg-[#000B18] p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-white text-sm font-medium">Column #{i + 1}</span>
            <Button type="button" size="sm" variant="ghost" className="h-6 px-2 text-[#FF1744]" onClick={() => removeCol(i)}>
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
          <Input
            value={c.title || ''}
            onChange={(e) => setCol(i, { title: e.target.value })}
            placeholder="Column title"
            className="bg-[#041A3B] border-white/15 text-white text-sm"
          />
          <Textarea
            value={c.content || ''}
            onChange={(e) => setCol(i, { content: e.target.value })}
            placeholder="Column body (HTML allowed)"
            rows={3}
            className="bg-[#041A3B] border-white/15 text-white text-sm"
          />
          <ImageUploadField
            label="Column Image (optional)"
            value={c.image || ''}
            onChange={(url) => setCol(i, { image: url })}
            api={api}
          />
          <div className="grid grid-cols-2 gap-2">
            <Input
              value={c.cta_label || ''}
              onChange={(e) => setCol(i, { cta_label: e.target.value })}
              placeholder="CTA label (optional)"
              className="bg-[#041A3B] border-white/15 text-white text-sm"
            />
            <Input
              value={c.cta_url || ''}
              onChange={(e) => setCol(i, { cta_url: e.target.value })}
              placeholder="CTA URL"
              className="bg-[#041A3B] border-white/15 text-white text-sm"
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------------- CTA buttons ---------------- */
function CTAButtons({ sectionForm, update }) {
  return (
    <div className="space-y-2 p-3 rounded-lg bg-[#000B18]/50 border border-white/5">
      <Label className="text-white text-xs font-medium">Call-to-Action Buttons (optional)</Label>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-[#5C7599] text-[11px]">Primary button label</Label>
          <Input value={sectionForm.cta_label || ''} onChange={(e) => update({ cta_label: e.target.value })} className="bg-[#000B18] border-white/20 text-white text-sm" placeholder="Get Started" data-testid="cta-label-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[#5C7599] text-[11px]">Primary URL</Label>
          <Input value={sectionForm.cta_url || ''} onChange={(e) => update({ cta_url: e.target.value })} className="bg-[#000B18] border-white/20 text-white text-sm" placeholder="/register" data-testid="cta-url-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[#5C7599] text-[11px]">Secondary button label</Label>
          <Input value={sectionForm.cta_secondary_label || ''} onChange={(e) => update({ cta_secondary_label: e.target.value })} className="bg-[#000B18] border-white/20 text-white text-sm" placeholder="Learn More" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[#5C7599] text-[11px]">Secondary URL</Label>
          <Input value={sectionForm.cta_secondary_url || ''} onChange={(e) => update({ cta_secondary_url: e.target.value })} className="bg-[#000B18] border-white/20 text-white text-sm" placeholder="/about" />
        </div>
      </div>
    </div>
  );
}

/* ---------------- Layout & Style controls ---------------- */
function LayoutStyleControls({ sectionForm, update, api }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="rounded-lg border border-white/10 bg-[#000B18]/40">
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full px-3 py-2 text-left flex items-center justify-between text-white text-sm font-medium hover:bg-white/5"
        data-testid="toggle-layout-style"
      >
        <span>Layout & Style</span>
        <span className="text-[#A0B4CC] text-xs">{expanded ? 'Hide ▴' : 'Show ▾'}</span>
      </button>

      {expanded && (
        <div className="p-3 space-y-3 border-t border-white/10">
          {/* Typography */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-[#5C7599] text-[11px]">Text Align</Label>
              <select
                value={sectionForm.text_align || 'left'}
                onChange={(e) => update({ text_align: e.target.value })}
                className="w-full bg-[#000B18] border border-white/20 text-white rounded-md px-2 py-1.5 text-sm"
                data-testid="text-align-select"
              >
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#5C7599] text-[11px]">Title Color</Label>
              <input
                type="color"
                value={sectionForm.title_color || '#FFFFFF'}
                onChange={(e) => update({ title_color: e.target.value })}
                className="w-full h-8 rounded border border-white/20 bg-transparent cursor-pointer"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#5C7599] text-[11px]">Body Color</Label>
              <input
                type="color"
                value={sectionForm.text_color || '#A0B4CC'}
                onChange={(e) => update({ text_color: e.target.value })}
                className="w-full h-8 rounded border border-white/20 bg-transparent cursor-pointer"
              />
            </div>
          </div>

          {/* Layout */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-[#5C7599] text-[11px]">Background Color</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={sectionForm.bg_color || '#041A3B'}
                  onChange={(e) => update({ bg_color: e.target.value })}
                  className="w-8 h-8 rounded border border-white/20 bg-transparent cursor-pointer"
                  data-testid="bg-color-picker"
                />
                <Input
                  value={sectionForm.bg_color || ''}
                  onChange={(e) => update({ bg_color: e.target.value })}
                  className="bg-[#000B18] border-white/20 text-white text-xs font-mono flex-1"
                  placeholder="transparent"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#5C7599] text-[11px]">Padding (vertical)</Label>
              <select
                value={sectionForm.padding_y || 'default'}
                onChange={(e) => update({ padding_y: e.target.value })}
                className="w-full bg-[#000B18] border border-white/20 text-white rounded-md px-2 py-1.5 text-sm"
                data-testid="padding-y-select"
              >
                <option value="none">None</option>
                <option value="small">Small</option>
                <option value="default">Default</option>
                <option value="large">Large</option>
                <option value="xlarge">Extra Large</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#5C7599] text-[11px]">Max Width</Label>
              <select
                value={sectionForm.max_width || 'default'}
                onChange={(e) => update({ max_width: e.target.value })}
                className="w-full bg-[#000B18] border border-white/20 text-white rounded-md px-2 py-1.5 text-sm"
                data-testid="max-width-select"
              >
                <option value="narrow">Narrow</option>
                <option value="default">Default</option>
                <option value="wide">Wide</option>
                <option value="full">Full width</option>
              </select>
            </div>
          </div>

          {/* Background image */}
          <ImageUploadField
            label="Background Image (optional)"
            value={sectionForm.background_image || ''}
            onChange={(url) => update({ background_image: url })}
            api={api}
          />

          {sectionForm.background_image && (
            <BackgroundOptions sectionForm={sectionForm} update={update} />
          )}

          {/* Rounded toggle */}
          <div className="flex items-center justify-between p-2 rounded bg-[#000B18]">
            <Label className="text-[#A0B4CC] text-xs">Rounded corners</Label>
            <Switch
              checked={sectionForm.rounded !== false}
              onCheckedChange={(checked) => update({ rounded: checked })}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function BackgroundOptions({ sectionForm, update }) {
  return (
    <>
      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <Label className="text-[#A0B4CC] text-xs">BG Position</Label>
          <select
            value={sectionForm.background_position || 'center center'}
            onChange={(e) => update({ background_position: e.target.value })}
            className="w-full bg-[#000B18] border border-white/20 text-white rounded-md px-2 py-1.5 text-sm"
            data-testid="bg-position-select"
          >
            {['center center','top center','top left','top right','center left','center right','bottom center','bottom left','bottom right'].map(v => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-[#A0B4CC] text-xs">BG Attachment</Label>
          <select
            value={sectionForm.background_attachment || 'scroll'}
            onChange={(e) => update({ background_attachment: e.target.value })}
            className="w-full bg-[#000B18] border border-white/20 text-white rounded-md px-2 py-1.5 text-sm"
            data-testid="bg-attachment-select"
          >
            <option value="scroll">Scroll</option>
            <option value="fixed">Fixed (Parallax)</option>
            <option value="local">Local</option>
          </select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-[#A0B4CC] text-xs">BG Size</Label>
          <select
            value={sectionForm.background_size || 'cover'}
            onChange={(e) => update({ background_size: e.target.value })}
            className="w-full bg-[#000B18] border border-white/20 text-white rounded-md px-2 py-1.5 text-sm"
            data-testid="bg-size-select"
          >
            <option value="cover">Cover</option>
            <option value="contain">Contain</option>
            <option value="auto">Auto</option>
            <option value="100% 100%">Stretch (100%)</option>
          </select>
        </div>
      </div>

      <div className="mt-2 p-3 rounded-lg bg-[#000B18]/50 border border-white/5 space-y-3">
        <Label className="text-[#A0B4CC] text-xs font-medium block">Overlay</Label>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-[#5C7599] text-[11px]">Color</Label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={sectionForm.overlay_color || '#000B18'}
                onChange={(e) => update({ overlay_color: e.target.value })}
                className="w-8 h-8 rounded border border-white/20 bg-transparent cursor-pointer"
              />
              <Input
                value={sectionForm.overlay_color || '#000B18'}
                onChange={(e) => update({ overlay_color: e.target.value })}
                className="bg-[#000B18] border-white/20 text-white text-xs font-mono flex-1"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[#5C7599] text-[11px]">Opacity ({Math.round((sectionForm.overlay_opacity ?? 0.8) * 100)}%)</Label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={sectionForm.overlay_opacity ?? 0.8}
              onChange={(e) => update({ overlay_opacity: parseFloat(e.target.value) })}
              className="w-full h-2 rounded-lg appearance-none cursor-pointer accent-[#00E5FF] bg-white/10"
            />
          </div>
        </div>
      </div>
    </>
  );
}
